import { CityInterface } from "@/api/ReferenceBooks/Lists/GEO/Cities/List/Interfaces/CityInterface";
import { CountryInterface } from "@/api/ReferenceBooks/Lists/GEO/Countries/List/Interfaces/CountryInterface";
import { PhoneUnitInterface } from "@/components/PhonesManager/Interfaces/PhoneUnitInterface";
import { notSelectedId } from "@/config/Other";
import { RootState } from "@/store/Store";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type PhoneFormState = "new" | "edit";

export interface ManagePhoneFormInterface {
    state: PhoneFormState,
    address: AddressInterface,
    phoneId: string,
    /** @deprecated */
    phone: string,
    title: string,
    open: boolean,
}

export interface AddressFormInterface {
    id: string,
    addressField: string,
    countryId: CountryInterface["id"],
    countryName: CountryInterface["name"],
    cityId: CityInterface["id"],
    cityName: CityInterface["name"],
}

function getInitialState() {
    return {
        addresses: [] as AddressInterface[],
        formStage: "new",
        form: {
            id: "",
            addressField: "",
            countryId: notSelectedId,
            countryName: "",
            cityId: notSelectedId,
            cityName: "",
        } as AddressFormInterface,
        /** @deprecated */
        managePhoneForm: {
            state: "new",
            address: {
                id: "",
                address: "",
            },
            phoneId: "",
            phone: "",
            title: "",
            open: false,
        } as ManagePhoneFormInterface,
    };
}

type InitialState = ReturnType<typeof getInitialState>;

export const PhysicalAddressSlice = createSlice({
    name: "PhysicalAddressSlice",
    initialState: getInitialState(),
    reducers: {
        setAddressField: (state, action) => {
            state.form.addressField = action.payload;
        },
        setAddressFormValue: <K extends keyof AddressFormInterface>(state: InitialState, action: PayloadAction<{name: K, value: AddressFormInterface[K]}>) => {
            state.form[action.payload.name] = action.payload.value;
        },
        clearAddressForm: (state: InitialState, action: PayloadAction<void>) => {
            const initialState = getInitialState();
            state.form = initialState.form;
        },
        addAddress: (state, action: PayloadAction<AddressFormInterface>) => {
            state.addresses.push({
                id: action.payload.id, 
                address: action.payload.addressField, 
                phones: [],
                country_id: action.payload.countryId,
                countryName: action.payload.countryName,
                city_id: action.payload.cityId,
                cityName: action.payload.cityName,
            });
        },
        editAddress: (state) => {
            const editingAddress = state.addresses.find(address => address.id === state.form.id) as AddressInterface;
            editingAddress.country_id = state.form.countryId;
            editingAddress.countryName = state.form.countryName;
            editingAddress.city_id = state.form.cityId;
            editingAddress.cityName = state.form.cityName;
            editingAddress.address = state.form.addressField;
        },
        deleteAddress: (state, action) => {
            const deletingAddressId = action.payload.id;
            state.addresses = state.addresses.filter(address => address.id !== deletingAddressId);
        },
        setAddressForm: (state, action: PayloadAction<AddressFormInterface>) => {
            state.form = action.payload;
        },
        setFormStage: (state, action) => {
            state.formStage = action.payload;
        },
        setToDefault: (state, action) => {
            state.addresses = [];
            state.formStage = FormStage.new;
            state.form.id = "";
            state.form.addressField = "";
        },
        // addPhoneToAnAddress: (state, action: PayloadAction<{addressId: AddressInterface["id"], phone: Phone}>) => {
        //     const address = state.addresses.find(unit => unit.id === action.payload.addressId);
        //     if (address !== undefined) {
        //         address.phones.push(action.payload.phone);
        //     }
        // },
        changePhoneForm: (state, action: PayloadAction<ManagePhoneFormInterface>) => {
            state.managePhoneForm = action.payload;
        },
        clearPhoneForm: (state, action: PayloadAction<void>) => {
            state.managePhoneForm.phone = "";
            state.managePhoneForm.title = "";
            state.managePhoneForm.phoneId = "";
            state.managePhoneForm.state = "new";
        },
        deletePhoneFromAddress: (state, action: PayloadAction<{addressId: AddressInterface["id"], phone: Phone["phone"]}>) => {
            const address = state.addresses.find(unit => unit.id === action.payload.addressId);
            if (address !== undefined) {
                address.phones = address.phones.filter(unit => unit.phone !== action.payload.phone);
            }
        },
        // changePhoneForAddress: (state, action: PayloadAction<{addressId: AddressInterface["id"], phone: Phone}>) => {
        //     const address = state.addresses.find(unit => unit.id === action.payload.addressId);
        //     if (address === undefined) {
        //         return;
        //     }
        //     address.phones = address.phones.map(unit => {
        //         if (unit.id === action.payload.phone.id) {
        //             return action.payload.phone;
        //         }

        //         return unit;
        //     });
        // },
        clearAddressForPhoneForm: (state, action: PayloadAction<void>) => {
            const initialState = getInitialState();
            state.managePhoneForm.address = initialState.managePhoneForm.address;
        },
        setPhonesForAddress: (state, action: PayloadAction<{addressId: AddressInterface["id"], phones: PhoneUnitInterface[]}>) => {
            state.addresses = state.addresses.map(address => {
                if (address.id !== action.payload.addressId) {
                    return address;
                }
                const newAddress = {...address};
                newAddress.phones = action.payload.phones;
                return newAddress;
            })
        },
    }
});

export const {
    setAddressField,
    addAddress,
    editAddress,
    setAddressForm,
    setFormStage,
    deleteAddress,
    setToDefault,
    changePhoneForm,
    // addPhoneToAnAddress,
    clearPhoneForm,
    deletePhoneFromAddress,
    // changePhoneForAddress,
    clearAddressForPhoneForm,
    setPhonesForAddress,
    setAddressFormValue,
    clearAddressForm,
} = PhysicalAddressSlice.actions;

export const addressesSelector = (state: RootState) => state.registerNewPartnerPhysicalAddress.addresses;
export const addressFieldSelector = (state: RootState) => state.registerNewPartnerPhysicalAddress.form.addressField;
export const addressFormSelector = (state: RootState) => state.registerNewPartnerPhysicalAddress.form;
export const formStageSelector = (state: RootState) => state.registerNewPartnerPhysicalAddress.formStage;
export const physicalAddressesAreCorrectSelector = (state: RootState) => state.registerNewPartnerPhysicalAddress.addresses.length > 0;
export const phoneFormSelector = (state: RootState) => state.registerNewPartnerPhysicalAddress.managePhoneForm;

export const RegisterNewPartnerPhysicalAddressReducer = PhysicalAddressSlice.reducer;

export interface AddressInterface {
    id: string,
    address: string,
    country_id: CountryInterface["id"],
    countryName: CountryInterface["name"],
    city_id: CityInterface["id"],
    cityName: CityInterface["name"],
    phones: PhoneUnitInterface[],
}

export interface Phone {
    id: string,
    phone: string,
    title: string,
}

export const FormStage = {
    new: "new",
    edit: "edit",
}